import { useState, useEffect } from "react";
import { CheckCircle, ChevronLeft, ChevronRight, Loader2, Mail } from "lucide-react";

const ZAPIER_WEBHOOK = "https://hooks.zapier.com/hooks/catch/28046347/421ruv1/";

const PROVINCES = [
  "Alberta", "British Columbia", "Manitoba", "New Brunswick",
  "Newfoundland and Labrador", "Nova Scotia", "Ontario", "Prince Edward Island",
  "Quebec", "Saskatchewan", "Northwest Territories", "Nunavut", "Yukon",
];

const CARRIERS = [
  "Canada Life", "Sun Life", "Manulife", "Equitable Life", "Desjardins",
  "Beneva", "RBC Insurance", "Green Shield", "Empire Life",
  "We don't have a plan yet", "Not sure", "Other",
];

const WHO_PAYS = [
  "Employer pays all of it", "Cost-shared with employees",
  "Employees pay all of it", "Not sure",
];

const COST_GOALS = [
  "Reduce what we're spending", "Keep cost similar but improve coverage",
  "Invest more for better benefits", "Not sure yet",
];

const TIMELINES = ["As soon as possible", "At our next renewal", "Just exploring for now"];

const INTEREST_OPTIONS = [
  "Virtual care (Consult+)", "EAP / mental health", "Health Spending Account",
  "Better drug / dental coverage", "More plan flexibility",
];

const STEPS = [
  "About your company",
  "How to reach you",
  "Your current plan",
  "Where you'd like to land",
  "Authorize the review",
];

const inputClass = "w-full px-3.5 py-3 border-[1.5px] border-[#DDD8CC] rounded text-sm text-[#1A2540] bg-white focus:border-[#C9A84C] focus:outline-none transition-colors";
const selectClass = `${inputClass} appearance-none pr-10`;
const labelClass = "block text-[11px] font-medium tracking-wider text-[#6B7A99] uppercase mb-1.5";

const GroupBenefitsReviewPage = () => {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [interestedIn, setInterestedIn] = useState([]);
  const [form, setForm] = useState({
    company: "", nature: "", province: "", years: "", employees: "",
    contactName: "", role: "", email: "", phone: "",
    carrier: "", renewalDate: "", whoPays: "", monthlyCost: "", advisor: "",
    costGoal: "", timeline: "",
    legalName: "", title: "", signature: "",
  });

  const signatureDate = new Date().toLocaleDateString("en-CA", {
    year: "numeric", month: "long", day: "numeric",
  });

  useEffect(() => {
    document.title = "Group Benefits Review | Mayker Financial Group";
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step, submitted]);

  const update = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const toggleInterest = (option) => {
    setInterestedIn((prev) =>
      prev.includes(option) ? prev.filter((o) => o !== option) : [...prev, option]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.signature.trim()) return;
    setSubmitting(true);

    const payload = {
      "Company": form.company,
      "Nature of business": form.nature,
      "Province": form.province,
      "Years in business": form.years,
      "Eligible employees": form.employees,
      "Contact name": form.contactName,
      "Role": form.role,
      "Email": form.email,
      "Phone": form.phone,
      "Current carrier": form.carrier,
      "Renewal date": form.renewalDate,
      "Who pays": form.whoPays,
      "Approx monthly premium": form.monthlyCost,
      "Current advisor": form.advisor,
      "Interested in": interestedIn.join(", "),
      "Cost goal": form.costGoal,
      "Timeline": form.timeline,
      "Signed by (name)": form.legalName,
      "Title (signer)": form.title,
      "Signature": form.signature,
      "Signature date": signatureDate,
      "Submitted at": new Date().toISOString(),
    };

    try {
      await fetch(ZAPIER_WEBHOOK, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    } catch (err) {
      // no-cors responses are opaque; submission still reaches the webhook
      console.warn("Webhook submit issue:", err);
    }
    setSubmitting(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAF8F3] flex items-center justify-center px-4 pt-20 pb-20" data-testid="gbr-thank-you">
        <div className="text-center max-w-lg">
          <div className="w-20 h-20 bg-[#C9A84C]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-[#C9A84C]" />
          </div>
          <h1 className="font-playfair text-3xl lg:text-4xl font-bold text-[#0C1B33] mb-4">
            {`Thank you — we've got it.`}
          </h1>
          <p className="text-base text-[#6B7A99] mb-6 leading-relaxed">
            {`You'll receive an email shortly with a mandate to e-sign, and we'll start preparing your benefits comparison.`}
          </p>
          <p className="text-sm text-[#6B7A99]">
            Questions? Reach Kai at{" "}
            <a href="mailto:kai@maykerfinancial.com" className="text-[#B08F3A] font-medium hover:underline">
              kai@maykerfinancial.com
            </a>.
          </p>
        </div>
      </div>
    );
  }

  const progressPct = ((step + 1) / STEPS.length) * 100;
  const signed = form.signature.trim().length > 0;

  return (
    <div className="min-h-screen bg-[#FAF8F3] pt-24 pb-20" data-testid="group-benefits-review-page">
      <div className="max-w-2xl mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="mb-8 text-center">
          <p className="text-xs font-medium tracking-[0.2em] uppercase text-[#C9A84C] mb-3">
            No cost · No obligation
          </p>
          <h1 className="font-playfair text-4xl lg:text-5xl font-bold text-[#0C1B33] mb-4">
            Group Benefits Review
          </h1>
          <p className="text-base text-[#6B7A99] leading-relaxed max-w-xl mx-auto">
            A few quick questions about your current group benefits plan — no cost, no
            obligation. Takes about 4 minutes.
          </p>
        </div>

        {/* Progress indicator */}
        <div className="mb-8" data-testid="gbr-progress">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-medium tracking-wider text-[#6B7A99] uppercase">
              Step {step + 1} of {STEPS.length}
            </span>
            <span className="text-[11px] font-medium text-[#0C1B33]">{STEPS[step]}</span>
          </div>
          <div className="h-1.5 bg-[#E7E2D6] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#C9A84C] rounded-full transition-all duration-300"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        {/* Card */}
        <form onSubmit={handleSubmit} className="bg-white border border-[#DDD8CC] rounded-lg p-6 lg:p-8 shadow-sm">
          <h2 className="font-playfair text-2xl font-semibold text-[#0C1B33] mb-6">
            {STEPS[step]}
          </h2>

          {/* Section 1 */}
          {step === 0 && (
            <div className="space-y-4" data-testid="gbr-section-1">
              <div>
                <label className={labelClass}>Company name</label>
                <input type="text" name="company" value={form.company} onChange={update} className={inputClass} data-testid="gbr-company" />
              </div>
              <div>
                <label className={labelClass}>What the business does</label>
                <input type="text" name="nature" value={form.nature} onChange={update} className={inputClass} data-testid="gbr-nature" />
              </div>
              <div>
                <label className={labelClass}>Province</label>
                <select name="province" value={form.province} onChange={update} className={selectClass} data-testid="gbr-province">
                  <option value="">Select...</option>
                  {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>Years in business <span className="lowercase tracking-normal normal-case text-[#9fb0c0]">(optional)</span></label>
                <input type="text" name="years" value={form.years} onChange={update} className={inputClass} data-testid="gbr-years" />
              </div>
              <div>
                <label className={labelClass}>Number of employees on the plan</label>
                <input type="number" name="employees" value={form.employees} onChange={update} className={inputClass} data-testid="gbr-employees" />
                <p className="text-[11px] text-[#9fb0c0] mt-1.5">Plans start at 3</p>
              </div>
            </div>
          )}

          {/* Section 2 */}
          {step === 1 && (
            <div className="space-y-4" data-testid="gbr-section-2">
              <div>
                <label className={labelClass}>Your name</label>
                <input type="text" name="contactName" value={form.contactName} onChange={update} className={inputClass} data-testid="gbr-contact-name" />
              </div>
              <div>
                <label className={labelClass}>Your role <span className="lowercase tracking-normal normal-case text-[#9fb0c0]">(optional)</span></label>
                <input type="text" name="role" value={form.role} onChange={update} className={inputClass} data-testid="gbr-role" />
              </div>
              <div>
                <label className={labelClass}>Email</label>
                <input type="email" name="email" value={form.email} onChange={update} className={inputClass} data-testid="gbr-email" />
              </div>
              <div>
                <label className={labelClass}>Phone <span className="lowercase tracking-normal normal-case text-[#9fb0c0]">(optional)</span></label>
                <input type="tel" name="phone" value={form.phone} onChange={update} className={inputClass} data-testid="gbr-phone" />
              </div>
            </div>
          )}

          {/* Section 3 */}
          {step === 2 && (
            <div className="space-y-4" data-testid="gbr-section-3">
              <div>
                <label className={labelClass}>Current carrier</label>
                <select name="carrier" value={form.carrier} onChange={update} className={selectClass} data-testid="gbr-carrier">
                  <option value="">Select...</option>
                  {CARRIERS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>Renewal date <span className="lowercase tracking-normal normal-case text-[#9fb0c0]">(optional)</span></label>
                <input type="date" name="renewalDate" value={form.renewalDate} onChange={update} className={inputClass} data-testid="gbr-renewal" />
              </div>
              <div>
                <label className={labelClass}>Who pays for the plan today?</label>
                <select name="whoPays" value={form.whoPays} onChange={update} className={selectClass} data-testid="gbr-who-pays">
                  <option value="">Select...</option>
                  {WHO_PAYS.map((w) => <option key={w} value={w}>{w}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>Roughly the monthly cost <span className="lowercase tracking-normal normal-case text-[#9fb0c0]">(optional)</span></label>
                <input type="text" name="monthlyCost" value={form.monthlyCost} onChange={update} className={inputClass} data-testid="gbr-monthly-cost" />
              </div>
              <div>
                <label className={labelClass}>Who advises / administers it now <span className="lowercase tracking-normal normal-case text-[#9fb0c0]">(optional)</span></label>
                <input type="text" name="advisor" value={form.advisor} onChange={update} className={inputClass} data-testid="gbr-advisor" />
              </div>
            </div>
          )}

          {/* Section 4 */}
          {step === 3 && (
            <div className="space-y-5" data-testid="gbr-section-4">
              <div>
                <label className={labelClass}>{`Anything you'd love to add or improve?`}</label>
                <div className="space-y-2.5 mt-1">
                  {INTEREST_OPTIONS.map((opt) => {
                    const checked = interestedIn.includes(opt);
                    return (
                      <button
                        type="button"
                        key={opt}
                        onClick={() => toggleInterest(opt)}
                        className={`w-full flex items-center gap-3 text-left px-4 py-3 rounded border-[1.5px] transition-colors text-sm ${
                          checked ? "border-[#C9A84C] bg-[#F6EFDA] text-[#0C1B33]" : "border-[#DDD8CC] bg-white text-[#1A2540] hover:border-[#C9A84C]"
                        }`}
                        data-testid={`gbr-interest-${opt}`}
                      >
                        <span className={`w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center ${checked ? "border-[#C9A84C] bg-[#C9A84C]" : "border-[#DDD8CC]"}`}>
                          {checked && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                        </span>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className={labelClass}>{`On cost, you'd ideally…`}</label>
                <select name="costGoal" value={form.costGoal} onChange={update} className={selectClass} data-testid="gbr-cost-goal">
                  <option value="">Select...</option>
                  {COST_GOALS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>Timeline</label>
                <select name="timeline" value={form.timeline} onChange={update} className={selectClass} data-testid="gbr-timeline">
                  <option value="">Select...</option>
                  {TIMELINES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* Section 5 */}
          {step === 4 && (
            <div className="space-y-4" data-testid="gbr-section-5">
              <div className="bg-[#FAF8F3] border border-[#DDD8CC] rounded-lg p-4 text-sm leading-relaxed text-[#3A4663]">
                {`I'd like a no-obligation review of our group benefits plan. I authorize Kai Simpson and AGA Benefit Solutions to study our current plan and to contact our current insurer, if needed, to obtain the plan and billing details required to prepare an accurate comparison. I understand a formal mandate may follow to confirm this, and that there's no cost or obligation.`}
              </div>
              <div>
                <label className={labelClass}>Full legal name</label>
                <input type="text" name="legalName" value={form.legalName} onChange={update} className={inputClass} data-testid="gbr-legal-name" />
              </div>
              <div>
                <label className={labelClass}>Title</label>
                <input type="text" name="title" value={form.title} onChange={update} className={inputClass} data-testid="gbr-signer-title" />
              </div>
              <div>
                <label className={labelClass}>Sign here</label>
                <input
                  type="text"
                  name="signature"
                  value={form.signature}
                  onChange={update}
                  placeholder="Type your name"
                  className="w-full px-3.5 py-3 border-[1.5px] border-[#DDD8CC] rounded text-2xl text-[#0C1B33] bg-white focus:border-[#C9A84C] focus:outline-none transition-colors"
                  style={{ fontFamily: "'Brush Script MT', 'Segoe Script', 'Snell Roundhand', cursive" }}
                  data-testid="gbr-signature"
                />
                <p className="text-[11px] text-[#9fb0c0] mt-1.5" data-testid="gbr-signature-date">
                  Electronically signed · {signatureDate}
                </p>
              </div>
            </div>
          )}

          {/* Nav buttons */}
          <div className="flex items-center justify-between gap-3 mt-8 pt-6 border-t border-[#EFEBE0]">
            {step > 0 ? (
              <button
                type="button"
                onClick={() => setStep((s) => s - 1)}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-[#6B7A99] hover:text-[#0C1B33] transition-colors"
                data-testid="gbr-back"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>
            ) : <span />}

            {step < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={() => setStep((s) => s + 1)}
                className="inline-flex items-center gap-1.5 bg-[#0C1B33] text-white text-sm font-medium tracking-wide px-6 py-3 rounded hover:bg-[#162647] transition-colors"
                data-testid="gbr-next"
              >
                Continue
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="submit"
                disabled={!signed || submitting}
                className="inline-flex items-center gap-2 bg-[#C9A84C] text-[#0C1B33] text-sm font-semibold tracking-wide px-6 py-3 rounded hover:bg-[#E8CC80] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="gbr-submit"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>
                ) : (
                  "Submit my review request"
                )}
              </button>
            )}
          </div>
        </form>

        <p className="flex items-center justify-center gap-2 mt-5 text-[12px] text-[#6B7A99]">
          <Mail className="w-3.5 h-3.5" />
          Questions? Reach Kai at kai@maykerfinancial.com
        </p>
      </div>
    </div>
  );
};

export default GroupBenefitsReviewPage;
